/**
 * @tenka/data の盤面を、core の Ruleset に変換する。
 *
 * core は依存ゼロを保つため @tenka/data を知らない（docs/03 §2）。
 * 両者を結ぶのはここ1箇所だけ。
 */

import * as data from '@tenka/data';
import { BOARD, TERRAIN_ARM, isProvince } from '@tenka/data';
import type {
  Effect,
  HistoricalEventSpec,
  Ruleset,
  SquareSpec,
  WarlordSpec,
} from '@tenka/core';

export * from '@tenka/core';

/** data の Square を core の SquareSpec に写す */
const toSpec = (square: (typeof BOARD)[number]): SquareSpec => {
  if (!isProvince(square)) {
    return { id: square.id, kind: square.kind as SquareSpec['kind'], name: square.name };
  }
  const zeni = (square.yields ?? [])
    .filter((y) => y.res === 'zeni')
    .reduce((sum, y) => sum + y.n, 0);
  return {
    id: square.id,
    kind: 'province',
    name: square.name,
    kokudaka: square.kokudaka,
    defense: square.defense,
    terrain: TERRAIN_ARM[square.terrain],
    ...(zeni > 0 ? { yields: zeni } : {}),
  };
};

/** @tenka/data から札を集めて id → 効果 の表にする */
function collectCards(): Record<string, readonly Effect[]> {
  const out: Record<string, readonly Effect[]> = {};
  for (const value of Object.values(data)) {
    for (const item of Array.isArray(value) ? value : [value]) {
      if (!item || typeof item !== 'object') continue;
      const card = item as { id?: string; type?: string; timing?: string; effects?: Effect[] };
      if (card.id && card.type && card.timing && Array.isArray(card.effects)) {
        out[card.id] = card.effects;
      }
    }
  }
  return out;
}

export const CARD_EFFECTS = collectCards();
export const CARD_IDS: readonly string[] = Object.keys(CARD_EFFECTS);

/**
 * 史実イベントを集める。
 *
 * **これが無いと35件は一度も発火しない。** 実際そうなっていた —
 * data に書かれ、`validate:data` も通り、バンドルにも載っていたのに、
 * `Ruleset` に渡す口が無かったので engine は存在すら知らなかった。
 * 「書いたものが動いている」ことは、動かして確かめないと分からない。
 *
 * 札と違って `type`/`timing` を持たず、`kind`/`chapter`/`year` を持つ。
 * その形で見分ける（`collectCards` が拾わないのもこのため）。
 */
function collectEvents(): HistoricalEventSpec[] {
  /*
   * **id で潰す。** data の barrel は同じイベントを個別の const としても
   * 章ごとの配列としても出すので、素直に集めると1件が2〜3件に増える
   * （実測で 35件 → 96件）。そのまま渡すと時代法が複数回発火し、
   * 事件札の引き当ても重複したぶんだけ偏る。
   * `collectCards` が Record を使っているのも同じ理由である。
   */
  const byId = new Map<string, HistoricalEventSpec>();
  for (const value of Object.values(data)) {
    for (const item of Array.isArray(value) ? value : [value]) {
      if (!item || typeof item !== 'object') continue;
      const e = item as Partial<data.HistoricalEvent>;
      if (
        typeof e.id === 'string' &&
        typeof e.name === 'string' &&
        typeof e.chapter === 'number' &&
        (e.kind === 'era-law' || e.kind === 'incident') &&
        Array.isArray(e.effects)
      ) {
        byId.set(e.id, {
          id: e.id,
          name: e.name,
          chapter: e.chapter,
          kind: e.kind,
          effects: e.effects,
        });
      }
    }
  }
  // id で並べて決定論を保つ。Object.values の順に依存すると、
  // data の export を足しただけで事件札の引き当てが変わる
  return [...byId.values()].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

export const HISTORICAL_EVENTS: readonly HistoricalEventSpec[] = collectEvents();

/** data の Warlord を core の WarlordSpec に写す */
const toWarlordSpec = (w: (typeof data.WARLORDS)[number]): WarlordSpec => ({
  id: w.id,
  name: w.name,
  abilityName: w.ability.name,
  ...(w.ability.rules ? { rules: w.ability.rules } : {}),
  ...(w.ability.holeCardRules ? { holeCardRules: w.ability.holeCardRules } : {}),
  ...(w.ability.deckInject ? { deckInject: w.ability.deckInject } : {}),
  ...(w.ability.castleDiscount ? { castleDiscount: w.ability.castleDiscount } : {}),
});

export const WARLORD_SPECS: readonly WarlordSpec[] = data.WARLORDS.map(toWarlordSpec);

export const RULESET: Ruleset = {
  board: BOARD.map(toSpec),
  cardEffects: CARD_EFFECTS,
  // 軍札の構成も data から注入する。core が持つのは既定値だけ
  decks: data.DECKS,
  // 武将の能力。これがないと8名は名前と開始国しか違わない
  warlords: WARLORD_SPECS,
  // 席順の補正。先手が空白の国を先に取れるぶんを埋める
  seatBonus: data.SEAT_BONUS,
  // 史実イベント。章の開始時の時代法と、評定マスで引く事件札
  events: HISTORICAL_EVENTS,
};

/**
 * 武将8名の席。id と開始国を1箇所から出す。
 * `START_SQUARES` と二重に持つと、片方だけ直したときに静かにずれる。
 */
export const WARLORD_SEATS: readonly { warlordId: string; startSquare: number }[] =
  data.WARLORDS.map((w) => ({ warlordId: w.id, startSquare: w.startSquare }));

/** 武将の開始国。docs/01 §8 */
export const START_SQUARES: readonly number[] = [
  4, // 織田信長  尾張
  21, // 武田信玄  甲斐
  19, // 上杉謙信  越後
  10, // 北条氏康  相模
  30, // 毛利元就  石見
  36, // 島津義久  薩摩
  16, // 伊達政宗  陸奥
  38, // 長宗我部元親 伊予
];

export type RulesetSquare = SquareSpec;
